/**
 * Custom-column cell for `assets` columns.
 *
 * Read-only column showing the containing project's asset review progress —
 * approved-count / total-count summed across the project's top-level Review &
 * Approvals folders. Assets hang off the project, not the brief, so every brief
 * in a project renders the same figure; the column header says so.
 * Visual mirrors the shared `<ApprovalProgressBar>` but
 * the layout truncates and hides the trailing text gracefully when the
 * column is narrower than the bar + label can fit (vs. spilling into the
 * next cell as the stock component does).
 *
 * Aggregation hook is per-project; multiple briefs in the same project share
 * the same TanStack query (same projectId → same queryKey).
 */
import { Progress } from '@monksflow/monks-ui';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useProjectApprovalAggregate } from '@/entities/project-folder';
import { ROUTES } from '@/shared/config/routes';

interface AssetsCustomCellProps {
  projectId: string;
}

const INDICATOR_FILL: Record<'empty' | 'in_progress' | 'complete', string> = {
  empty: '[&>[data-slot=progress-indicator]]:bg-muted-foreground',
  in_progress: '[&>[data-slot=progress-indicator]]:bg-amber-500',
  complete: '[&>[data-slot=progress-indicator]]:bg-green-500',
};

export function AssetsCustomCell({ projectId }: AssetsCustomCellProps) {
  const { t } = useTranslation('trackers');
  const { t: tProjects } = useTranslation('projects');

  const navigate = useNavigate();
  const aggregate = useProjectApprovalAggregate(projectId);
  const total = aggregate.totalCount;
  const approved = aggregate.approvedCount;
  const percent = total === 0 ? 0 : Math.round((approved / total) * 100);

  const visualStatus: keyof typeof INDICATOR_FILL =
    total === 0 ? 'empty' : approved === total ? 'complete' : 'in_progress';

  const label =
    total === 0
      ? tProjects('projectFolder.approvalUtils.noAssetsUploaded')
      : tProjects('projectFolder.approvalUtils.approvedOfTotal', { approved, total });

  return (
    // `inline-flex` lets the bar + label group size to its natural content
    // when there's room (so the parent cell's `justify-center` centers it),
    // while `max-w-full min-w-0` allows it to shrink and the label to
    // truncate when the column is narrower than the natural width.
    // Wrapped in a button so the whole cell click navigates to the
    // project's Review & Approvals (folders) page.
    <button
      type="button"
      aria-label={t('trackersBoard.assetsCustomCell.openTheProjectSReview', { label })}
      title={t('trackersBoard.assetsCustomCell.openTheProjectSReview', { label })}
      onClick={(e) => {
        e.stopPropagation();
        void navigate(ROUTES.PROJECT_FOLDERS(projectId));
      }}
      className="inline-flex max-w-full min-w-0 cursor-pointer items-center gap-2 rounded px-1 py-0.5 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Progress value={percent} className={`h-2 w-16 shrink-0 ${INDICATOR_FILL[visualStatus]}`} />
      {/* Label shrinks down to zero — when the column is too narrow for
         both, the bar wins and the label simply disappears instead of
         overflowing the cell. */}
      <span className="min-w-0 truncate text-xs text-muted-foreground">{label}</span>
    </button>
  );
}
